/**
 * Route-level code splitting. The demo and the app used to ship every screen (and every library, lesson and QR reader)
 * in one 2.6 MB file, so Today waited on code it never runs. A screen now loads when its route opens; Today, the frame
 * and sign-in stay in the first file. `prefetchScreens` warms the tab screens once the browser is idle, after Today has
 * painted, so a tap on a tab is still instant. It never runs before first content and never blocks an action.
 */
import { Suspense, createElement, lazy, useEffect, type ComponentType, type LazyExoticComponent, type ReactNode } from "react";

/** A screen's file would not load: the page was opened before an update (its old file names are gone) or the network dropped. */
export function isChunkLoadError(e: unknown): boolean {
  const m = e instanceof Error ? `${e.name} ${e.message}` : String(e);
  return /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|ChunkLoadError/i.test(m);
}

/** Try the screen's file once more after a moment (a dropped connection); a real failure still throws. */
export async function importWithRetry<T>(load: () => Promise<T>, waitMs = 400): Promise<T> {
  try { return await load(); } catch (e) {
    if (!isChunkLoadError(e)) throw e;
    await new Promise((r) => setTimeout(r, waitMs));
    return load();
  }
}

export function lazyNamed<M extends Record<string, unknown>, K extends keyof M>(
  load: () => Promise<M>,
  name: K,
): LazyExoticComponent<M[K] extends ComponentType<any> ? M[K] : never> {
  return lazy(async () => ({ default: (await importWithRetry(load))[name] as M[K] extends ComponentType<any> ? M[K] : never }));
}

/**
 * A piece of a screen that is not needed to paint it (a sheet that opens on a tap, a hint under a finding): its file loads when it is
 * first drawn, and `fallback` (nothing, by default) shows until then. The component keeps its name and props, so the caller does not change.
 */
export function lazyInline<M extends Record<string, unknown>, K extends keyof M>(
  load: () => Promise<M>,
  name: K,
  fallback: ReactNode = null,
): M[K] extends ComponentType<infer P> ? ComponentType<P> : never {
  const Inner = lazyNamed(load, name) as unknown as ComponentType<any>;
  const Wrapped = (props: Record<string, unknown>) => createElement(Suspense, { fallback }, createElement(Inner, props));
  return Wrapped as never;
}

/** Import these after first content, one at a time, when idle. No-op where there is no idle callback (tests, old browsers). */
export function prefetchScreens(loaders: Array<() => Promise<unknown>>): () => void {
  const w = globalThis as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
  if (!w.requestIdleCallback) return () => {};
  let stopped = false;
  let handle = 0;
  const next = (i: number) => {
    if (stopped || i >= loaders.length) return;
    handle = w.requestIdleCallback!(() => { void loaders[i]!().catch(() => {}).finally(() => next(i + 1)); }, { timeout: 4000 });
  };
  next(0);
  return () => { stopped = true; w.cancelIdleCallback?.(handle); };
}

/** The tab screens, warmed once Today's own content is on screen (`ready`), never while it is still loading. */
export function usePrefetchTabs(ready: boolean): void {
  useEffect(() => {
    if (!ready) return;
    return prefetchScreens([() => import("../screens/Do.tsx"), () => import("../screens/Report.tsx"), () => import("../screens/Learn.tsx"), () => import("../screens/Me.tsx"), () => import("../screens/Team.tsx")]);
  }, [ready]);
}
